"use client";

import { Callout, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import { Form } from "radix-ui";
import * as React from "react";
import { type ReactNode } from "react";
import { Button, Dialog, Label, PasswordField } from "./elements.js";
import { useUpdatePassword } from "../api/endpoint.js";
import { SaveButton } from "./save-button.js";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";

interface ChangePasswordDialogProps extends Dialog.RootProps {
  children?: ReactNode;
}

export function ChangePasswordDialog({
  children,
  ...props
}: ChangePasswordDialogProps) {
  const [open, setOpen] = React.useState(false);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <Dialog.Root
      {...props}
      open={props.open || open}
      onOpenChange={props.onOpenChange || setOpen}
    >
      <Dialog.Trigger>{children}</Dialog.Trigger>

      <Dialog.Content maxWidth="480px">
        <Content onClose={handleClose} />
      </Dialog.Content>
    </Dialog.Root>
  );
}

interface ContentProps {
  onClose: () => void;
}

function Content({ onClose }: ContentProps) {
  const translate = useTranslation();
  const changePassword = useUpdatePassword({
    mutation: {
      onError: (error) => {
        const errorMsg = getMutationErrorMessage(error);
        if (errorMsg === "Invalid credentials") {
          setServerErrors({
            currentPassword:
              "Your current password is incorrect. Please, try again.",
          });
        }
      },
    },
  });
  const [disableSubmit, setDisableSubmit] = React.useState(true);
  const [serverErrors, setServerErrors] = React.useState<{
    currentPassword?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const errorMessage = React.useMemo(() => {
    if (changePassword.error) {
      return getMutationErrorMessage(changePassword.error);
    }

    return null;
  }, [changePassword.error]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const currentPassword = formData.get("oldPassword")?.toString() ?? "";
    const newPassword = formData.get("password")?.toString() ?? "";

    changePassword.mutate({
      data: { currentPassword, newPassword },
    });
  };

  return (
    <>
      <Dialog.Title mb="5">
        <Translation
          defaultMessage="Change Password"
          id="kAYx7z"
          description="Dialog title for changing password"
        />
      </Dialog.Title>
      <VisuallyHidden>
        <Dialog.Description>
          <Translation
            defaultMessage="Change your account password"
            id="R5rXuR"
            description="Dialog description for changing password"
          />
        </Dialog.Description>
      </VisuallyHidden>

      {errorMessage && errorMessage !== "Invalid credentials" ? (
        <Callout.Root color="red" my="-2">
          <Callout.Text>{errorMessage}</Callout.Text>
        </Callout.Root>
      ) : null}

      <Form.Root
        onSubmit={handleSubmit}
        onChange={(event) => {
          const formData = new FormData(event.currentTarget);
          const currentPassword = formData.get("oldPassword")?.toString() ?? "";
          const newPassword = formData.get("password")?.toString() ?? "";
          const confirmPassword =
            formData.get("confirmPassword")?.toString() ?? "";

          setDisableSubmit(
            currentPassword === "" ||
              newPassword === "" ||
              confirmPassword === "",
          );
          setServerErrors({});
        }}
        onClearServerErrors={() => {
          setServerErrors({});
        }}
      >
        <Flex mt="5" direction="column" gap="4">
          <Form.Field
            name="oldPassword"
            asChild
            serverInvalid={!serverErrors.currentPassword}
          >
            <Flex direction="column" gap="2">
              <Form.Label asChild>
                <Label>
                  <Translation
                    defaultMessage="Enter your current password"
                    id="uqn8py"
                    description="Label for current password field"
                  />
                </Label>
              </Form.Label>

              <Form.Control asChild>
                <PasswordField
                  autoFocus
                  required
                  disabled={
                    changePassword.isPending || changePassword.isSuccess
                  }
                  autoComplete="current-password"
                  placeholder={translate({
                    defaultMessage: "Current password",
                    id: "P2oB4G",
                    description: "Placeholder for current password field",
                  })}
                />
              </Form.Control>

              <Form.Message match="valueMissing" asChild>
                <Text size="2" color="red">
                  <Translation
                    defaultMessage="Please enter your current password"
                    id="QKO3st"
                    description="Error message when current password is missing"
                  />
                </Text>
              </Form.Message>

              {serverErrors.currentPassword && (
                <Form.Message asChild>
                  <Text size="2" color="red">
                    {serverErrors.currentPassword}
                  </Text>
                </Form.Message>
              )}
            </Flex>
          </Form.Field>

          <Form.Field
            name="password"
            asChild
            serverInvalid={!serverErrors.password}
          >
            <Flex direction="column" gap="2">
              <Form.Label asChild>
                <Label>
                  <Translation
                    defaultMessage="Enter your new password"
                    id="b/+TBS"
                    description="Label for new password field"
                  />
                </Label>
              </Form.Label>

              <Form.Control asChild>
                <PasswordField
                  autoComplete="new-password"
                  placeholder={translate({
                    defaultMessage: "New password",
                    id: "8d23GS",
                    description: "Placeholder for new password field",
                  })}
                  required
                  disabled={
                    changePassword.isPending || changePassword.isSuccess
                  }
                />
              </Form.Control>

              <Form.Message match="valueMissing" asChild>
                <Text size="2" color="red">
                  <Translation
                    defaultMessage="Please enter your new password"
                    id="L4yLAf"
                    description="Error message when new password is missing"
                  />
                </Text>
              </Form.Message>
              <Form.Message match={(value) => value.length < 8} asChild>
                <Text size="2" color="red">
                  <Translation
                    defaultMessage="Password must be at least 8 characters"
                    id="WRjyrh"
                    description="Error message when password is too short"
                  />
                </Text>
              </Form.Message>
              <Form.Message
                match={(value, formData) =>
                  value === formData.get("oldPassword")
                }
                asChild
              >
                <Text size="2" color="red">
                  <Translation
                    defaultMessage="You cannot reuse your current password"
                    id="mEkhOO"
                    description="Error message when user tries to reuse current password"
                  />
                </Text>
              </Form.Message>
            </Flex>
          </Form.Field>

          <Form.Field
            name="confirmPassword"
            asChild
            serverInvalid={!serverErrors.confirmPassword}
          >
            <Flex direction="column" gap="2">
              <Form.Label asChild>
                <Label>
                  <Translation
                    defaultMessage="Confirm your new password"
                    id="K83lBn"
                    description="Label for password confirmation field"
                  />
                </Label>
              </Form.Label>

              <Form.Control asChild>
                <PasswordField
                  autoComplete="new-password"
                  placeholder={translate({
                    defaultMessage: "Confirm new password",
                    id: "cubpBN",
                    description: "Placeholder for password confirmation field",
                  })}
                  required
                  disabled={
                    changePassword.isPending || changePassword.isSuccess
                  }
                />
              </Form.Control>
              <Form.Message match="valueMissing" asChild>
                <Text size="2" color="red">
                  <Translation
                    defaultMessage="Please confirm your new password"
                    id="fXZhqG"
                    description="Error message when password confirmation is missing"
                  />
                </Text>
              </Form.Message>
              <Form.Message
                match={(value, formData) => value !== formData.get("password")}
                asChild
              >
                <Text size="2" color="red">
                  <Translation
                    defaultMessage="The new passwords you entered don't match."
                    id="Zdq2Hr"
                    description="Error message when passwords don't match"
                  />
                </Text>
              </Form.Message>
            </Flex>
          </Form.Field>
        </Flex>

        <Flex mt="5" gap="3" justify="end">
          <Dialog.Close>
            <Button
              variant="secondary"
              type="button"
              disabled={changePassword.isPending || changePassword.isSuccess}
            >
              <Translation
                defaultMessage="Cancel"
                id="hHNj31"
                description="Cancel button text"
              />
            </Button>
          </Dialog.Close>

          <SaveButton
            type="submit"
            loading={changePassword.isPending}
            done={changePassword.isSuccess}
            onDone={onClose}
            disabled={disableSubmit || undefined}
          >
            <Translation
              defaultMessage="Change password"
              id="8GDtZu"
              description="Submit button text to change password"
            />
          </SaveButton>
        </Flex>
      </Form.Root>

      {/* mirror errors in a live region */}
      <VisuallyHidden asChild>
        <section aria-live="polite">{errorMessage}</section>
      </VisuallyHidden>
    </>
  );
}

function getMutationErrorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}

// Note: Error messages in getMutationErrorMessage are kept as plain strings
// since they come from the API and are displayed dynamically
